import { Dropdown, DropdownProps } from 'components/dropdown/Dropdown';
import { DropdownDataSource } from 'components/dropdown/DropdownPopup';
import { getDropdownTriggerIconFullWidth } from 'components/dropdown/styled';
import { BaseInlineEditorProps, InlineEditor } from 'components/inline_edit/InlineEditor';
import { useInlineEditor } from 'custom_hooks/useInlineEditor';
import { ReactNode, useRef } from 'react';
import styled from 'styled-components';

export type DropdownInlineEditorProps<TValue> = BaseInlineEditorProps<TValue, TValue> &
    Readonly<{
        /**
         * The dropdown options.
         */
        options: TValue[] | DropdownDataSource<TValue> | undefined;
        /**
         * Dropdown config.
         */
        dropdownConfig: Omit<
            DropdownProps<TValue>,
            'data' | 'value' | 'onChange' | 'controlRef' | 'trigger'
        >;

        confirmDisabled?: (val: TValue) => boolean;
    }>;

export const DropdownValueWrapper = styled.span`
    display: inline-flex;
    flex-wrap: nowrap;
    white-space: nowrap;

    // hacky pseudo-element to emulate the width of the chevron
    // 'ButtonLabelIcon' in 'DropdownTrigger', to prevent layout shift upon
    // enter/exit-ing edit mode
    &::after {
        content: '';
        display: inline-block;
        visibility: hidden;
        width: ${getDropdownTriggerIconFullWidth()};
    }
`;

export function DropdownInlineEditor<TValue>({
    value,
    renderValue,
    validate,
    onConfirm,
    onClose,
    isDisabled,
    editorClass,
    editorStyle,
    options,
    dropdownConfig,
    confirmDisabled,
}: DropdownInlineEditorProps<TValue>) {
    const popupRef = useRef<HTMLDivElement>(null);
    const {
        valueToRender,
        inputValue,
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useInlineEditor<TValue>({
        value,
        validate,
        onConfirm,
        onClose,
    });

    const confirmExplicitlyDisabled =
        confirmDisabled !== undefined &&
        inputValue !== undefined &&
        confirmDisabled(inputValue);

    return (
        <InlineEditor
            value={
                <DropdownValueWrapper>
                    {renderValue
                        ? renderValue(valueToRender)
                        : // I'm not sure this is necessarily true... but preserving what this did pre-react 18
                          (valueToRender as ReactNode)}
                </DropdownValueWrapper>
            }
            editor={
                <Dropdown<TValue>
                    {...dropdownConfig}
                    data={options}
                    value={inputValue}
                    onChange={setInputValue}
                    isDisabled={dropdownConfig.isDisabled}
                    trigger={{ className: 'full-width' }}
                    popup={{
                        popupRef,
                        ...dropdownConfig.popup,
                    }}
                />
            }
            editorClass={editorClass}
            editorStyle={editorStyle}
            error={error}
            canClose={!isConfirming}
            canEdit={!isConfirming}
            canConfirm={!confirmExplicitlyDisabled && canConfirm}
            isEditing={isEditing}
            isDisabled={isDisabled || isConfirming}
            isPending={isConfirming}
            onClose={closeEditor}
            onEdit={openEditor}
            onConfirm={confirm}
            contentRefs={[popupRef]}
        />
    );
}
